import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPipeline, updatePipelineStage, addLeadToPipeline, getLeads } from '../api';
import { safeErr } from '../App';

const STAGES = [
  { id: 'interested',        label: 'Interested',         color: 'var(--blue)',   bg: 'var(--blue-bg)'   },
  { id: 'meeting_scheduled', label: 'Meeting Scheduled',  color: 'var(--amber)',  bg: 'var(--amber-bg)'  },
  { id: 'proposal_sent',     label: 'Proposal Sent',      color: '#7c3aed',       bg: '#f5f3ff'          },
  { id: 'negotiation',       label: 'Negotiation',        color: 'var(--orange)', bg: '#fff7ed'          },
  { id: 'won',               label: 'Won ✓',              color: 'var(--green)',  bg: 'var(--green-bg)'  },
  { id: 'lost',              label: 'Lost',               color: 'var(--red)',    bg: 'var(--red-bg)'    },
];

function Card({ lead, onMove, isDragging, onDragStart, onDragEnd }) {
  const nav = useNavigate();
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const menuRef = useRef(null);
  const initials = (lead.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  useEffect(() => {
    const close = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMoveMenu(false); };
    if (showMoveMenu) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showMoveMenu]);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)', padding: '12px', marginBottom: 8,
        cursor: 'grab', opacity: isDragging ? 0.4 : 1,
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
        transition: 'box-shadow .15s, opacity .15s',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div className="avatar av-sm av-blue" style={{ flexShrink: 0, fontSize: 11 }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontWeight: 600, fontSize: 13, cursor: 'pointer', color: 'var(--blue)' }}
            onClick={() => nav(`/leads/${lead.id}`)}
          >
            {lead.name}
          </div>
          {lead.company && (
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lead.role ? `${lead.role} · ` : ''}{lead.company}
            </div>
          )}
        </div>
        {/* Move button */}
        <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            className="btn btn-ghost btn-icon"
            style={{ fontSize: 14, padding: '2px 5px', lineHeight: 1 }}
            onClick={e => { e.stopPropagation(); setShowMoveMenu(v => !v); }}
            title="Move to stage"
          >⋯</button>
          {showMoveMenu && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', zIndex: 100,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-lg)', boxShadow: '0 4px 16px rgba(0,0,0,.12)',
              minWidth: 170, padding: '4px 0',
            }}>
              {STAGES.map(s => (
                <button key={s.id}
                  onClick={e => { e.stopPropagation(); onMove(lead.id, s.id); setShowMoveMenu(false); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '7px 14px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 12, color: s.color, fontWeight: 500,
                  }}
                >{s.label}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Reply snippet */}
      {lead.reply_snippet && (
        <div style={{
          fontSize: 11, color: 'var(--text-2)', background: 'var(--surface2)',
          borderRadius: 6, padding: '6px 8px', marginBottom: 8,
          lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          "{lead.reply_snippet}"
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--text-3)' }}>{lead.email}</span>
        {lead.reply_at && (
          <span style={{ fontSize: 10, color: 'var(--text-3)' }}>
            {new Date(lead.reply_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        )}
      </div>
    </div>
  );
}

function Column({ stage, cards, onMove, dragOverStage, onDragOver, onDrop, draggingId, onCardDragStart }) {
  return (
    <div
      onDragOver={e => { e.preventDefault(); onDragOver(stage.id); }}
      onDrop={() => onDrop(stage.id)}
      style={{
        minWidth: 240, width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column',
        background: dragOverStage === stage.id ? stage.bg : 'var(--surface2)',
        borderRadius: 'var(--r-lg)', border: '2px solid',
        borderColor: dragOverStage === stage.id ? stage.color : 'transparent',
        padding: '0 0 8px', transition: 'background .15s, border-color .15s',
      }}
    >
      {/* Column header */}
      <div style={{
        padding: '12px 14px 10px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: '1px solid var(--border)',
        marginBottom: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color, flexShrink: 0 }}/>
          <span style={{ fontWeight: 600, fontSize: 12 }}>{stage.label}</span>
        </div>
        <span style={{
          background: stage.bg, color: stage.color, borderRadius: 10,
          padding: '1px 7px', fontSize: 11, fontWeight: 700,
        }}>{cards.length}</span>
      </div>

      {/* Cards */}
      <div style={{ padding: '0 8px', flex: 1, minHeight: 80 }}>
        {cards.map(lead => (
          <Card key={lead.id} lead={lead}
            isDragging={draggingId === lead.id}
            onDragStart={() => onCardDragStart(lead.id, stage.id)}
            onDragEnd={() => {}}
            onMove={onMove}
          />
        ))}
        {cards.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '24px 0', color: 'var(--text-3)',
            fontSize: 12, borderRadius: 8,
            border: dragOverStage === stage.id ? `2px dashed ${stage.color}` : '2px dashed var(--border)',
          }}>Drop here</div>
        )}
      </div>
    </div>
  );
}

function AddToPipelineModal({ onClose, onAdded, showToast }) {
  const [search,   setSearch]  = useState('');
  const [results,  setResults] = useState([]);
  const [selected, setSelected]= useState(null);
  const [stage,    setStage]   = useState('interested');
  const [saving,   setSaving]  = useState(false);
  const [searching,setSearching]= useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await getLeads({ search: search.trim(), limit: 10 });
        // Exclude leads already in pipeline
        setResults((r.data || []).filter(l => l.interest !== 'interested'));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [search]);

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await addLeadToPipeline(selected.id, stage);
      showToast(`${selected.name} added to pipeline`);
      onAdded();
    } catch (e) {
      showToast(safeErr(e, 'Failed to add to pipeline'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ width: 480 }}>
        <div className="modal-header">
          <span className="modal-title">Add client to pipeline</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Search */}
          <div className="form-group">
            <label className="form-label">Search lead by name or company</label>
            <input
              className="form-input"
              placeholder="e.g. Dr Patrick, Sano Genetics…"
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null); }}
              autoFocus
            />
          </div>

          {/* Results */}
          {searching && <div style={{ textAlign: 'center', padding: 12 }}><span className="spinner spinner-sm"/></div>}
          {!searching && results.length > 0 && !selected && (
            <div style={{
              border: '1px solid var(--border)', borderRadius: 'var(--r)',
              overflow: 'hidden', marginBottom: 12, maxHeight: 220, overflowY: 'auto',
            }}>
              {results.map(l => (
                <div key={l.id}
                  onClick={() => { setSelected(l); setSearch(l.name); setResults([]); }}
                  style={{
                    padding: '9px 12px', cursor: 'pointer', fontSize: 13,
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div className="avatar av-sm av-blue" style={{ fontSize: 11, flexShrink: 0 }}>
                    {(l.name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{l.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                      {[l.role, l.company].filter(Boolean).join(' · ')} · {l.email}
                    </div>
                  </div>
                  <span className={`pill s-${l.status||'new'}`} style={{ marginLeft: 'auto', fontSize: 10 }}>{l.status||'new'}</span>
                </div>
              ))}
            </div>
          )}
          {!searching && search && results.length === 0 && !selected && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
              No leads found — try a different name or company.
            </div>
          )}

          {/* Selected lead confirmation */}
          {selected && (
            <div style={{
              background: 'var(--blue-bg)', border: '1px solid var(--blue-border)',
              borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div className="avatar av-sm av-blue" style={{ fontSize: 11, flexShrink: 0 }}>
                {selected.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{selected.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{selected.company} · {selected.email}</div>
              </div>
              <button className="btn btn-ghost btn-icon" style={{ fontSize: 11 }}
                onClick={() => { setSelected(null); setSearch(''); }}>✕</button>
            </div>
          )}

          {/* Stage picker */}
          <div className="form-group">
            <label className="form-label">Add to stage</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6 }}>
              {STAGES.map(s => (
                <div key={s.id} onClick={() => setStage(s.id)} style={{
                  padding: '7px 10px', borderRadius: 'var(--r)', cursor: 'pointer', fontSize: 12,
                  border: `2px solid ${stage === s.id ? s.color : 'var(--border)'}`,
                  background: stage === s.id ? s.bg : 'var(--surface)',
                  color: stage === s.id ? s.color : 'var(--text-2)',
                  fontWeight: stage === s.id ? 600 : 400, textAlign: 'center',
                  transition: 'all .12s',
                }}>
                  {s.label}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" disabled={!selected || saving} onClick={handleAdd}>
            {saving ? <><span className="spinner spinner-sm"/> Adding…</> : 'Add to pipeline'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Pipeline({ showToast }) {
  const [stages, setStages]           = useState({});
  const [loading, setLoading]         = useState(true);
  const [total, setTotal]             = useState(0);
  const [dragLeadId, setDragLeadId]   = useState(null);
  const [dragFromStage, setDragFromStage] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [showAddModal, setShowAddModal]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getPipeline();
      setStages(r.data.stages);
      setTotal(r.data.total);
    } catch (e) {
      showToast(safeErr(e, 'Failed to load pipeline'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleMove = async (leadId, targetStage) => {
    // Optimistic update
    setStages(prev => {
      const next = { ...prev };
      let movedLead = null;
      for (const s of STAGES) {
        const idx = (next[s.id] || []).findIndex(l => l.id === leadId);
        if (idx !== -1) { movedLead = next[s.id][idx]; next[s.id] = next[s.id].filter((_,i) => i !== idx); break; }
      }
      if (movedLead) next[targetStage] = [{ ...movedLead, pipeline_stage: targetStage }, ...(next[targetStage] || [])];
      return next;
    });
    try {
      await updatePipelineStage(leadId, targetStage);
    } catch (e) {
      showToast(safeErr(e, 'Failed to move card'), 'error');
      load();
    }
  };

  const handleDragStart = (leadId, fromStage) => {
    setDragLeadId(leadId);
    setDragFromStage(fromStage);
  };

  const handleDrop = (targetStage) => {
    if (dragLeadId && targetStage && targetStage !== dragFromStage) {
      handleMove(dragLeadId, targetStage);
    }
    setDragLeadId(null);
    setDragFromStage(null);
    setDragOverStage(null);
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg"/></div>;

  return (
    <div>
      {showAddModal && (
        <AddToPipelineModal
          showToast={showToast}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); load(); }}
        />
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAddModal(true)}>
          + Add client
        </button>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          ['Total interested', total],
          ['Won', (stages.won || []).length],
          ['In progress', STAGES.slice(0, 4).reduce((a, s) => a + (stages[s.id] || []).length, 0)],
        ].map(([label, val]) => (
          <div key={label} className="metric-card" style={{ marginBottom: 0, flex: '1 1 120px' }}>
            <div className="metric-label">{label}</div>
            <div className="metric-value">{val}</div>
          </div>
        ))}
      </div>

      {total === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎯</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No interested leads yet</div>
            <div style={{ color: 'var(--text-2)', fontSize: 13 }}>
              Leads that reply with interest will appear here automatically.
            </div>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', paddingBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, minWidth: 'max-content' }}>
            {STAGES.map(stage => (
              <Column
                key={stage.id}
                stage={stage}
                cards={stages[stage.id] || []}
                onMove={handleMove}
                draggingId={dragLeadId}
                dragOverStage={dragOverStage}
                onDragOver={(stageId) => setDragOverStage(stageId)}
                onDrop={handleDrop}
                onCardDragStart={handleDragStart}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
